import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { trackError } from '../telemetry/events'
import { getTelemetry } from '../telemetry/TelemetryProvider'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught:', error, info)
    try {
      getTelemetry().reporter.captureException(error, { extra: { componentStack: info.componentStack ?? '' } })
    } catch {
      // Never throw from error tracking
    }
    try {
      trackError(error, { componentStack: info.componentStack ?? undefined })
    } catch {
      // Never throw from error tracking
    }
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }
      return (
        <div className="min-h-screen bg-void flex items-center justify-center px-4">
          <div className="bg-obsidian border border-carbon rounded p-8 max-w-lg w-full">
            <h1 className="text-title-1 text-chalk mb-2">Something went wrong</h1>
            <p className="text-body-sm text-steel mb-4">
              {this.state.error.message}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="text-body-sm text-signal hover:underline"
            >
              ← Back to home
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
